import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { rescheduleCampaign } from '../utils/campaign';
import {
  toastSuccessConfig,
  toastErrorConfig,
} from '../components/shared/constants/toaster-config';

const SUCCESS_MESSAGE = 'Campaña reprogramada';

const ERROR_CODE_MESSAGES = {
  BROADCAST_NOT_FOUND: 'No se encontró la campaña',
  CAMPAIGN_NOT_FOUND: 'No se encontró la campaña',
  BROADCAST_NOT_SCHEDULED: 'Solo se pueden reprogramar campañas agendadas',
  RESCHEDULE_DATE_REQUIRED: 'Selecciona una nueva fecha de envío',
  RESCHEDULE_DATE_INVALID: 'La fecha seleccionada no es válida',
  RESCHEDULE_DATE_IN_PAST: 'La fecha de envío es en el pasado',
};

const UNEXPECTED_CODES = new Set(['SERVER_ERROR']);

function resolveErrorMessage(error) {
  // The axios interceptor sets message = data?.userMessage || data.
  // For backend responses shaped as { error: CODE }, message is the full
  // response data object { error: CODE }.
  const code =
    (error.message && typeof error.message === 'object' && error.message.error) ||
    error.internalCode ||
    null;

  if (code && ERROR_CODE_MESSAGES[code]) {
    return { message: ERROR_CODE_MESSAGES[code], isExpected: true };
  }

  if (code && UNEXPECTED_CODES.has(code)) {
    return { message: 'Ocurrió un error en el servidor. Intenta nuevamente.', isExpected: false };
  }

  return { message: 'Ocurrió un error inesperado. Intenta nuevamente.', isExpected: false };
}

/**
 * Hook that exposes the reschedule action for a SCHEDULED campaign.
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback invoked with the new sendAt string after a successful reschedule.
 * @returns {{ reschedule: Function, isRescheduling: boolean }}
 */
export const useCampaignReschedule = ({ onSuccess } = {}) => {
  const [isRescheduling, setIsRescheduling] = useState(false);

  /**
   * Moves a scheduled campaign to a new send time.
   * @param {string} campaignId - Campaign identifier.
   * @param {string} sendAt - ISO-like datetime string ("YYYY-MM-DDTHH:mm:ss"). No timezone
   *   suffix — server treats it as local time.
   * @returns {Promise<boolean>} true if the reschedule succeeded, false otherwise.
   */
  const reschedule = useCallback(async (campaignId, sendAt) => {
    setIsRescheduling(true);
    try {
      await rescheduleCampaign(campaignId, sendAt);
      toast.success(SUCCESS_MESSAGE, toastSuccessConfig);
      if (onSuccess) {
        onSuccess(sendAt);
      }
      return true;
    } catch (error) {
      const { message, isExpected } = resolveErrorMessage(error);
      toast.error(message, toastErrorConfig);
      if (!isExpected) {
        Sentry.captureException(error, {
          tags: { component: 'BroadcastAnalitycs', op: 'rescheduleCampaign' },
        });
      }
      return false;
    } finally {
      setIsRescheduling(false);
    }
  }, [onSuccess]);

  return { reschedule, isRescheduling };
};

export default useCampaignReschedule;
